import { c as _c } from "react/compiler-runtime";
import { memo, useCallback, useEffect, useRef, useState, type MouseEvent, type RefObject } from "react";
import Image from "next/image";
import { ExternalLink, X } from "lucide-react";
import { useTheme } from "next-themes";
import { usePostHog } from "posthog-js/react";
import { toast } from "sonner";
import addToSlackIllustrationDark from "@/assets/add_to_slack_illustration_dark.svg";
import addToSlackIllustrationLight from "@/assets/add_to_slack_illustration_light.svg";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useSidebarFeaturedBoxVerticalFit } from "@/hooks/useSidebarFeaturedBoxVerticalFit";
import { useActiveGithubOrgForUser } from "@/hooks/useConnectedGithubOrgs";
import { SLACK_INSTALL_SOURCE_SIDEBAR_FEATURED_BOX, useSlackInstallUrl, useSlackLinkStatus, useSlackWorkspaceStatus } from "@/hooks/useSlackIntegration";
import { useSlackOAuthPopup } from "@/hooks/useSlackOAuthPopup";
import { SLACK_INSTALL_EVENTS } from "@/lib/posthog-constants";
export interface SidebarSlackFeaturedBoxViewProps {
  isDark: boolean;
  isConnecting?: boolean;
  onConnect: () => void;
  /** When provided, renders a dismiss (X) button in the top-right corner. */
  onDismiss?: () => void;
}
export const SidebarSlackFeaturedBoxView = memo(function SidebarSlackFeaturedBoxView(t0) {
  const $ = _c(12);
  const {
    isDark,
    isConnecting: t1,
    onConnect,
    onDismiss
  } = t0;
  const isConnecting = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== onDismiss) {
    t2 = onDismiss && <button type="button" onClick={onDismiss} aria-label="Dismiss" className="absolute right-1.5 top-1.5 flex size-5 items-center justify-center rounded text-neutral-400 transition-colors hover:bg-black/[0.05] hover:text-neutral-600 dark:hover:bg-white/[0.08] dark:hover:text-neutral-200"><X className="size-3.5" /></button>;
    $[0] = onDismiss;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = isDark ? addToSlackIllustrationDark : addToSlackIllustrationLight;
  let t4;
  if ($[2] !== t3) {
    t4 = <Image src={t3} alt="" width={106} height={28} className="h-7 w-[106px] shrink-0" priority={false} />;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <p className="type-body-xs-regular m-0 max-w-[220px] px-1 text-center text-neutral-700 dark:text-neutral-400">Tag [code]smith from Slack to delegate tasks, kick off investigations and more</p>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== isConnecting || $[6] !== onConnect) {
    t6 = <Button type="button" variant="default" disabled={isConnecting} onClick={onConnect} className="cursor-pointer">Add to Slack</Button>;
    $[5] = isConnecting;
    $[6] = onConnect;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t2 || $[9] !== t4 || $[10] !== t6) {
    t7 = <div className="relative flex flex-col items-center gap-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-inset)] px-3 py-4">{t2}{t4}{t5}{t6}</div>;
    $[8] = t2;
    $[9] = t4;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
});

// Persisted so dismissing the box in one sidebar (GitHub Actions or Codesmith)
// keeps it hidden everywhere and across reloads.
const SLACK_FEATURED_BOX_DISMISSED_KEY = "slack-featured-box-dismissed";
export interface SidebarSlackFeaturedBoxProps {
  codesmithEnabled: boolean;
}
export interface SidebarSlackFeaturedBoxPlacementProps {
  codesmithEnabled: boolean;
  footerAnchorRef: RefObject<HTMLElement | null>;
  navBlockRef: RefObject<HTMLElement | null>;
  visible: boolean;
}
export const SidebarSlackFeaturedBoxPlacement = memo(function SidebarSlackFeaturedBoxPlacement(t0) {
  const $ = _c(2);
  const {
    codesmithEnabled,
    footerAnchorRef,
    navBlockRef,
    visible
  } = t0;
  const hasVerticalSpace = useSidebarFeaturedBoxVerticalFit(navBlockRef, footerAnchorRef, visible);
  if (!hasVerticalSpace) {
    return null;
  }
  let t1;
  if ($[0] !== codesmithEnabled) {
    t1 = <div className="-mx-1 mt-auto pb-2 pt-4 group-data-[state=collapsed]:hidden"><SidebarSlackFeaturedBox codesmithEnabled={codesmithEnabled} /></div>;
    $[0] = codesmithEnabled;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
});
export const SidebarSlackFeaturedBox = memo(function SidebarSlackFeaturedBox(t0) {
  const $ = _c(59);
  const {
    codesmithEnabled
  } = t0;
  const {
    resolvedTheme
  } = useTheme();
  const isDark = resolvedTheme === "dark";
  const posthog = usePostHog();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  const {
    data: workspaceStatus,
    isLoading: isSlackLoading,
    isFetching: isWorkspaceStatusFetching
  } = useSlackWorkspaceStatus();
  const isSlackConnected = workspaceStatus?.installed ?? false;
  const [dismissed, setDismissed] = useState(_temp);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setDismissed(true);
      try {
        localStorage.setItem(SLACK_FEATURED_BOX_DISMISSED_KEY, "1");
      } catch {}
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleDismiss = t1;
  const shouldShow = codesmithEnabled && !isSlackLoading && !isSlackConnected && !dismissed;
  const {
    data: installUrl
  } = useSlackInstallUrl(shouldShow, SLACK_INSTALL_SOURCE_SIDEBAR_FEATURED_BOX, undefined, true);
  const [linkFlowActive, setLinkFlowActive] = useState(false);
  const [showLinkPrompt, setShowLinkPrompt] = useState(false);
  const connectInitiatedRef = useRef(false);
  const chainStartedRef = useRef(false);
  const [installFlowSettled, setInstallFlowSettled] = useState(true);
  const installSettledAtRef = useRef(0);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      connectInitiatedRef.current = false;
      chainStartedRef.current = false;
      installSettledAtRef.current = 0;
      setInstallFlowSettled(true);
      setLinkFlowActive(false);
      setShowLinkPrompt(false);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== activeGithubOrg) {
    t3 = [activeGithubOrg];
    $[2] = activeGithubOrg;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  useEffect(t2, t3);
  const t4 = shouldShow || linkFlowActive;
  let t5;
  if ($[4] !== t4) {
    t5 = {
      enabled: t4
    };
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const {
    data: linkStatus,
    isError: isLinkStatusError,
    dataUpdatedAt: linkStatusUpdatedAt
  } = useSlackLinkStatus(undefined, t5);
  const linkUrl = linkStatus?.link_url;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [["slack-workspace-status"], ["slack-link-status"]];
    t7 = () => {
      installSettledAtRef.current = Date.now();
      setInstallFlowSettled(true);
      toast.success("Slack connected, you can now assign tasks via Slack");
    };
    t8 = reason => {
      installSettledAtRef.current = Date.now();
      setInstallFlowSettled(true);
      toast.error(reason ?? "Failed to connect Slack workspace");
    };
    t9 = () => {
      installSettledAtRef.current = Date.now();
      setInstallFlowSettled(true);
    };
    $[6] = t6;
    $[7] = t7;
    $[8] = t8;
    $[9] = t9;
  } else {
    t6 = $[6];
    t7 = $[7];
    t8 = $[8];
    t9 = $[9];
  }
  let t10;
  if ($[10] !== linkStatus) {
    t10 = () => linkStatus && !linkStatus.linked ? linkStatus.link_url : null;
    $[10] = linkStatus;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  let t12;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = [["slack-link-status"]];
    t12 = () => {
      chainStartedRef.current = true;
      setLinkFlowActive(true);
      setShowLinkPrompt(false);
    };
    $[12] = t11;
    $[13] = t12;
  } else {
    t11 = $[12];
    t12 = $[13];
  }
  let t13;
  let t14;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = reason_0 => {
      toast.error(reason_0 ?? "Failed to link Slack account");
      setShowLinkPrompt(true);
    };
    t14 = () => setShowLinkPrompt(true);
    $[14] = t13;
    $[15] = t14;
  } else {
    t13 = $[14];
    t14 = $[15];
  }
  let t15;
  if ($[16] !== t10) {
    t15 = {
      popupName: "sidebar-slack-featured-box",
      queryKeysToInvalidate: t6,
      onSuccess: t7,
      onError: t8,
      onClose: t9,
      chain: {
        getChainUrl: t10,
        queryKeysToInvalidate: t11,
        onStart: t12,
        onSuccess: _temp2,
        onError: t13,
        onClose: t14
      }
    };
    $[16] = t10;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  const {
    openPopup
  } = useSlackOAuthPopup(t15);
  let t16;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      popupName: "sidebar-slack-link",
      queryKeysToInvalidate: [["slack-link-status"]],
      onSuccess: _temp3,
      onError: _temp4
    };
    $[18] = t16;
  } else {
    t16 = $[18];
  }
  const {
    openPopup: openLinkPopup
  } = useSlackOAuthPopup(t16);
  let t17;
  let t18;
  if ($[19] !== installFlowSettled || $[20] !== isWorkspaceStatusFetching || $[21] !== workspaceStatus) {
    t17 = () => {
      if (!connectInitiatedRef.current || !installFlowSettled) {
        return;
      }
      if (isWorkspaceStatusFetching) {
        return;
      }
      if (workspaceStatus !== undefined && !workspaceStatus.installed) {
        connectInitiatedRef.current = false;
      }
    };
    t18 = [installFlowSettled, isWorkspaceStatusFetching, workspaceStatus];
    $[19] = installFlowSettled;
    $[20] = isWorkspaceStatusFetching;
    $[21] = workspaceStatus;
    $[22] = t17;
    $[23] = t18;
  } else {
    t17 = $[22];
    t18 = $[23];
  }
  useEffect(t17, t18);
  let t19;
  let t20;
  if ($[24] !== installFlowSettled || $[25] !== isLinkStatusError || $[26] !== isSlackConnected || $[27] !== linkStatus || $[28] !== linkStatusUpdatedAt || $[29] !== workspaceStatus) {
    t19 = () => {
      if (!connectInitiatedRef.current) {
        return;
      }
      if (workspaceStatus === undefined || !isSlackConnected) {
        return;
      }
      if (chainStartedRef.current) {
        connectInitiatedRef.current = false;
        return;
      }
      setLinkFlowActive(true);
      if (!installFlowSettled) {
        return;
      }
      if (isLinkStatusError) {
        connectInitiatedRef.current = false;
        return;
      }
      if (linkStatus === undefined || (linkStatusUpdatedAt ?? 0) < installSettledAtRef.current) {
        return;
      }
      connectInitiatedRef.current = false;
      if (!linkStatus.linked) {
        setShowLinkPrompt(true);
      }
    };
    t20 = [isSlackConnected, workspaceStatus, linkStatus, linkStatusUpdatedAt, isLinkStatusError, installFlowSettled];
    $[24] = installFlowSettled;
    $[25] = isLinkStatusError;
    $[26] = isSlackConnected;
    $[27] = linkStatus;
    $[28] = linkStatusUpdatedAt;
    $[29] = workspaceStatus;
    $[30] = t19;
    $[31] = t20;
  } else {
    t19 = $[30];
    t20 = $[31];
  }
  useEffect(t19, t20);
  let t21;
  if ($[32] !== linkStatus?.linked) {
    t21 = () => {
      if (linkStatus?.linked) {
        setShowLinkPrompt(false);
      }
    };
    $[32] = linkStatus?.linked;
    $[33] = t21;
  } else {
    t21 = $[33];
  }
  const t22 = linkStatus?.linked;
  let t23;
  if ($[34] !== t22) {
    t23 = [t22];
    $[34] = t22;
    $[35] = t23;
  } else {
    t23 = $[35];
  }
  useEffect(t21, t23);
  let t24;
  if ($[36] !== installUrl || $[37] !== openPopup || $[38] !== posthog) {
    t24 = () => {
      if (!installUrl) {
        return;
      }
      posthog?.capture(SLACK_INSTALL_EVENTS.SIDEBAR_FEATURED_BOX_CLICK, {
        source: SLACK_INSTALL_SOURCE_SIDEBAR_FEATURED_BOX
      });
      chainStartedRef.current = false;
      const opened = openPopup(installUrl);
      connectInitiatedRef.current = opened;
      setInstallFlowSettled(!opened);
    };
    $[36] = installUrl;
    $[37] = openPopup;
    $[38] = posthog;
    $[39] = t24;
  } else {
    t24 = $[39];
  }
  const handleConnect = t24;
  let t25;
  if ($[40] !== linkUrl || $[41] !== openLinkPopup) {
    t25 = event => {
      if (!linkUrl) {
        event.preventDefault();
        return;
      }
      if (!openLinkPopup(linkUrl)) {
        event.preventDefault();
        toast.error("Popup blocked. Allow popups for this site, then try again.");
      }
    };
    $[40] = linkUrl;
    $[41] = openLinkPopup;
    $[42] = t25;
  } else {
    t25 = $[42];
  }
  const handleLinkFromPrompt = t25;
  if (!shouldShow && !showLinkPrompt) {
    return null;
  }
  let t26;
  if ($[43] !== handleConnect || $[44] !== installUrl || $[45] !== isDark || $[46] !== shouldShow) {
    t26 = shouldShow && <SidebarSlackFeaturedBoxView isDark={isDark} isConnecting={!installUrl} onConnect={handleConnect} onDismiss={handleDismiss} />;
    $[43] = handleConnect;
    $[44] = installUrl;
    $[45] = isDark;
    $[46] = shouldShow;
    $[47] = t26;
  } else {
    t26 = $[47];
  }
  let t27;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <AlertDialogHeader><AlertDialogTitle>Connect your Slack account</AlertDialogTitle><AlertDialogDescription>Slack is connected to your workspace. Link your Slack account so [code]smith can map your prompts to your Blacksmith user and GitHub permissions.</AlertDialogDescription></AlertDialogHeader>;
    $[48] = t27;
  } else {
    t27 = $[48];
  }
  let t28;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <AlertDialogCancel>Not now</AlertDialogCancel>;
    $[49] = t28;
  } else {
    t28 = $[49];
  }
  let t29;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <ExternalLink className="mr-1 size-4" />;
    $[50] = t29;
  } else {
    t29 = $[50];
  }
  let t30;
  if ($[51] !== handleLinkFromPrompt) {
    t30 = <AlertDialogContent>{t27}<AlertDialogFooter>{t28}<AlertDialogAction onClick={handleLinkFromPrompt}>{t29}Continue to Slack</AlertDialogAction></AlertDialogFooter></AlertDialogContent>;
    $[51] = handleLinkFromPrompt;
    $[52] = t30;
  } else {
    t30 = $[52];
  }
  let t31;
  if ($[53] !== showLinkPrompt || $[54] !== t30) {
    t31 = <AlertDialog open={showLinkPrompt} onOpenChange={setShowLinkPrompt}>{t30}</AlertDialog>;
    $[53] = showLinkPrompt;
    $[54] = t30;
    $[55] = t31;
  } else {
    t31 = $[55];
  }
  let t32;
  if ($[56] !== t26 || $[57] !== t31) {
    t32 = <>{t26}{t31}</>;
    $[56] = t26;
    $[57] = t31;
    $[58] = t32;
  } else {
    t32 = $[58];
  }
  return t32;
});
function _temp() {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    return localStorage.getItem(SLACK_FEATURED_BOX_DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}
function _temp2() {
  return toast.success("Slack account linked");
}
function _temp3() {
  return toast.success("Slack account linked");
}
function _temp4(reason_1) {
  return toast.error(reason_1 ?? "Failed to link Slack account");
}